/**
 * Cloudflare Turnstile widget (H-2 bot challenge).
 *
 * Graceful: renders nothing unless VITE_TURNSTILE_SITEKEY is set at build time,
 * so the forms work unchanged until the widget is provisioned. Uses explicit
 * rendering (reliable in a SPA where the form mounts after navigation) and
 * surfaces the token via onToken — '' when unsolved/expired/errored.
 */
import { useEffect, useImperativeHandle, useRef, type Ref } from 'react';

const SITEKEY = import.meta.env.VITE_TURNSTILE_SITEKEY as string | undefined;
const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

interface TurnstileApi {
  render: (el: HTMLElement, opts: Record<string, unknown>) => string;
  reset: (id?: string) => void;
  remove: (id?: string) => void;
}
declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

let scriptPromise: Promise<void> | null = null;
function ensureScript(): Promise<void> {
  if (typeof window === 'undefined') return Promise.resolve();
  if (window.turnstile) return Promise.resolve();
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise<void>((resolve, reject) => {
    const s = document.createElement('script');
    s.src = SCRIPT_SRC;
    s.async = true;
    s.defer = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error('turnstile script failed to load'));
    document.head.appendChild(s);
  });
  return scriptPromise;
}

export interface TurnstileHandle {
  /** Asks Cloudflare for a fresh token. Tokens are single-use, so call this after
   *  any submit that did not succeed, or the next attempt is rejected. */
  reset: () => void;
}

export interface TurnstileWidgetProps {
  /** Receives the token, or '' when it expires or is reset. */
  onToken: (token: string) => void;
  /** Called when the widget cannot produce a token (script blocked, widget error). */
  onFailure?: () => void;
  ref?: Ref<TurnstileHandle>;
}

export function TurnstileWidget({ onToken, onFailure, ref }: TurnstileWidgetProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);
  // Callbacks are read through a ref so the widget renders once per mount and a
  // parent re-render never tears down a token the visitor is about to use.
  const callbacks = useRef({ onToken, onFailure });
  useEffect(() => {
    callbacks.current = { onToken, onFailure };
  });

  useImperativeHandle(
    ref,
    () => ({
      reset: () => {
        callbacks.current.onToken('');
        if (widgetIdRef.current && window.turnstile) {
          window.turnstile.reset(widgetIdRef.current);
        }
      },
    }),
    [],
  );

  useEffect(() => {
    if (!SITEKEY || !containerRef.current) return;
    let cancelled = false;
    const fail = () => {
      callbacks.current.onToken('');
      callbacks.current.onFailure?.();
    };
    ensureScript()
      .then(() => {
        if (cancelled || !containerRef.current || !window.turnstile) return;
        widgetIdRef.current = window.turnstile.render(containerRef.current, {
          sitekey: SITEKEY,
          callback: (token: string) => callbacks.current.onToken(token),
          'expired-callback': () => callbacks.current.onToken(''),
          'error-callback': fail,
        });
      })
      .catch(fail);
    return () => {
      cancelled = true;
      if (widgetIdRef.current && window.turnstile) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch {
          /* widget already gone */
        }
        widgetIdRef.current = null;
      }
    };
  }, []);

  if (!SITEKEY) return null;
  // Cloudflare renders its own labelled iframe inside; no ARIA needed on the host.
  return <div ref={containerRef} className="cf-turnstile-widget" />;
}

/** True when Turnstile is configured for this build — lets callers require a token. */
export const TURNSTILE_ENABLED = Boolean(SITEKEY);
